
import { getShinseiList } from "@/api/shinsei.api";
import { dateFormat } from "@/utils/date";
import { defineComponent, onActivated, reactive, ref } from "vue";
import { tableHeader, tableExpand, ShinseiList } from "./config";
import { Kengen } from "@/constants/role.constant";
import { ELEMENT_ACCESS } from "@/constants/accessList.constant";
import dayjs from "dayjs";
import { useUserStore } from "@/store";
import useTable from "@/hooks/useTable";
import { QueryShinsei } from "@/interfaces/shinsei.interface";
import { useRouter } from "vue-router";
import { getKigyonaiShinchokuList, getStatusList } from "@/api/masterTable.api";
import {
  kigyonaiShinchokuMaster,
  shinseiStatusMaster,
} from "@/interfaces/masterTable.interface";
import { statusFilter, export2Excel } from "./common";
import { DatePattern, Shinchoku, Status } from "@/constants/common.constant";
import { SHINSEI_LIST } from "@/constants/componentName.constant";
const MAXIMUM_LEFT_DAY = 3;
export default defineComponent({
  name: SHINSEI_LIST,
  setup() {
    // ステータスリスト
    const statusOptions = ref<shinseiStatusMaster[]>();
    getStatusList().then((res) => {
      statusOptions.value = res;
    });
    // 企業内進捗リスト
    const shinchokuShinseiOptions = ref<kigyonaiShinchokuMaster[]>();
    const shinchokuShoninOptions = ref<kigyonaiShinchokuMaster[]>();
    getKigyonaiShinchokuList().then((res) => {
      shinchokuShinseiOptions.value = res.filter(
        (item) =>
          item.kigyonai_shinchoku_cd === Shinchoku.保存 ||
          item.kigyonai_shinchoku_cd === Shinchoku.仮申請 ||
          item.kigyonai_shinchoku_cd === Shinchoku.取消
      );
      shinchokuShoninOptions.value = res.filter(
        (item) =>
          item.kigyonai_shinchoku_cd === Shinchoku.仮保留 ||
          item.kigyonai_shinchoku_cd === Shinchoku.仮承認 ||
          item.kigyonai_shinchoku_cd === Shinchoku.取消
      );
    });

    const showDetailSearch = ref(false); //詳細検索欄表示制御

    const table = reactive({
      queryForm: {
        startDate: null,
        statusCd: "",
        kigyouShinchokuShinseiCd: "",
        kigyouShinchokuShoninCd: "",
        kaishaNm: "",
        groupNm: "",
        kjnNm: "",
        bushoNm: "",
        itakuKaishaNm: "",
        saiItakuKaishaNm: "",
        riyoMokuteki: "",
        endDate: null,
        page: 1,
        limit: 20,
      } as QueryShinsei,
      list: [] as ShinseiList[],
      total: 0,
      loading: true,
    });
    const userStore = useUserStore();
    const isShinseisha = userStore.isShinseisha;

    // テーブル表示項目制御
    let tableHeaderConfig;
    if (isShinseisha) {
      tableHeaderConfig = tableHeader.filter(
        (item) => !["kigyonaiShinchokuNmShonin", "kaishaNm"].includes(item.prop)
      );
    } else {
      tableHeaderConfig = tableHeader.filter(
        (item) => item.prop !== "kigyonaiShinchokuNmShinsei"
      );
    }

    // 検索処理
    /* 
        elementPlusのDatePickerコントロールに値を入力したあとに、tabキーで他のコントロールに移動する場合、
        DatePickerのv-modelにDate型を格納できず、dayjs型を格納してしまうため、
        dayjs型の検索条件の開始日、終了日をDATE型に変換
    */
    const normalizeQueryDate = () => {
      if (table.queryForm.startDate) {
        if (!(table.queryForm.startDate[0] instanceof Date)) {
          table.queryForm.startDate[0] = (
            table.queryForm.startDate[0] as unknown as dayjs.Dayjs
          ).toDate();
          table.queryForm.startDate[1] = (
            table.queryForm.startDate[1] as unknown as dayjs.Dayjs
          ).toDate();
        }
      }
      if (table.queryForm.endDate) {
        if (!(table.queryForm.endDate[0] instanceof Date)) {
          table.queryForm.endDate[0] = (
            table.queryForm.endDate[0] as unknown as dayjs.Dayjs
          ).toDate();
          table.queryForm.endDate[1] = (
            table.queryForm.endDate[1] as unknown as dayjs.Dayjs
          ).toDate();
        }
      }
    };
    const getList = async () => {
      table.loading = true;
      table.list = [];
      normalizeQueryDate();
      const { data } = await getShinseiList(table.queryForm);
      const { listData, total } = data.result;
      table.total = total;
      for (let i = 0; i < listData.length; i++) {
        table.list.push({
          id: listData[i].s_id,
          shinseiId: listData[i].s_shinsei_id,
          statusCd: listData[i].status_status_cd,
          statusNm: listData[i].status_status,
          kigyonaiShinchokuNmShinsei: listData[i].kss1_kigyonai_shinchoku,
          kigyonaiShinchokuNmShonin: listData[i].kss2_kigyonai_shinchoku,
          kikanKaishibi: listData[i].s_ss_kikan_kaishibi
            ? dateFormat(
                listData[i].s_ss_kikan_kaishibi,
                DatePattern.DatePattern
              )
            : null,
          kikanShuryobi: listData[i].s_ss_kikan_shuryobi
            ? dateFormat(
                listData[i].s_ss_kikan_shuryobi,
                DatePattern.DatePattern
              )
            : null,
          kaishaNm: listData[i].tsk_kaisha_nm,
          kaishaGroupNm: listData[i].tskg_kaisha_group_nm,
          torokuKjnNm: listData[i].ts_kjn_nm,
          torokuBushoNm: listData[i].ts_busho_nm,
          itakusakiKaishaNm: listData[i].s_ss_itakusaki_kaisha_nm,
          saiItakusakiKaishaNm: listData[i].s_ss_saiitakusaki_kaisha_nm,
          riyomokuteki: listData[i].s_ss_riyomokuteki,
          torokuDate: dateFormat(listData[i].s_toroku_date),
          koshinDate: dateFormat(listData[i].s_koshin_date),
        });
      }

      table.loading = false;
    };

    const tableRef = ref();
    const { handleSizeChange, handleSearch, handleClear } = useTable(
      table,
      getList,
      tableRef
    );

    // 申請登録画面 or 申請編集画面に遷移するのを制御
    const router = useRouter();
    const goToDetail = (id?: string) => {
      if (id) {
        router.push({ name: "shinseiDetail", params: { id } });
      } else {
        router.push({ name: "shinseiDetail" });
      }
    };

    //keep-aliveコンポーネントがアクティブ化されたときに呼び出されます
    onActivated(() => {
      getList();
    });

    // excel出力
    const { exportLoading, handleExport2excel } = export2Excel(table.queryForm);

    const tableRowClassName = ({
      row,
      rowIndex,
    }: {
      row: ShinseiList;
      rowIndex: number;
    }) => {
      let className = "";
      //striped row
      if (rowIndex % 2 !== 0) {
        className = "isStripedRow";
      }

      //deadline row
      if (
        row.kikanKaishibi &&
        [Status.申請中, Status.保留].includes(row.statusCd as Status) &&
        dayjs(row.kikanKaishibi).diff(dayjs().startOf("day"), "day") <=
          MAXIMUM_LEFT_DAY
      ) {
        className = "isFacingDeadlineRow";
      }
      return className;
    };

    return {
      table,
      tableHeaderConfig,
      tableExpand,
      getList,
      handleClear,
      handleSearch,
      handleSizeChange,
      Kengen,
      ELEMENT_ACCESS,
      handleExport2excel,
      exportLoading,
      statusOptions,
      shinchokuShinseiOptions,
      shinchokuShoninOptions,
      showDetailSearch,
      statusFilter,
      goToDetail,
      isShinseisha,
      tableRef,
      tableRowClassName,
    };
  },
});
